<template>
  <v-card flat>
    <!-- Скелетон для загрузки -->
    <template v-if="isLoading">
      <v-card-text class="form-wrapper">
        <div v-for="i in 3" :key="i" class="form-row">
          <div class="form-label">
            <v-skeleton-loader type="text" width="100" />
          </div>
          <v-skeleton-loader type="text" width="100%" />
        </div>
        <div class="form-row">
          <v-skeleton-loader type="card" width="100%" />
        </div>
      </v-card-text>
    </template>

    <!-- Основной контент -->
    <template v-else>
      <v-alert
        variant="tonal"
        v-if="alertMessage"
        :type="alertType"
        :text="alertMessage"
        density="compact"
        closable
        @click:close="clearAlert"
      />

      <v-card-text class="form-wrapper">
        <div class="form-row">
          <div class="form-label">СНИЛС</div>
          <v-mask-input
            v-model="student.snils"
            variant="outlined"
            density="compact"
            mask="###-###-### ##"
            placeholder="___-___-___ __"
            @update:modelValue="onFieldUpdate('snils', $event)"
          />
        </div>

        <div class="form-row">
          <div class="form-label">Паспортные данные</div>
          <passport-fields
            ref="passportFields"
            v-model="passportData"
            density="compact"
            :hide-details="false"
            :required="true"
            @update:modelValue="onPassportUpdate"
          />
        </div>

        <div class="form-row" v-if="loading">
          <div class="form-label">Прикрепленные файлы</div>
          <v-skeleton-loader type="list-item-three-line" />
        </div>

        <div class="form-row" v-else-if="scan && scan.length">
          <div class="form-label">Прикрепленные файлы</div>
          <v-list density="compact" class="scan-list">
            <v-list-item v-for="item in scan" :key="item.id" class="scan-item">
              <template #prepend>
                <v-icon icon="mdi-file-document-outline" color="primary" />
              </template>

              <v-list-item-title class="document-title">
                {{ getDocumentNameById(item.document_id) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ item.data }}
              </v-list-item-subtitle>

              <template #append>
                <v-menu location="start">
                  <template #activator="{ props }">
                    <v-btn
                      icon="mdi-dots-vertical"
                      variant="text"
                      size="small"
                      v-bind="props"
                    />
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="downloadFile(item)">
                      <template #prepend>
                        <v-icon icon="mdi-download" />
                      </template>
                      <v-list-item-title>Скачать</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteScan(item)">
                      <template #prepend>
                        <v-icon icon="mdi-delete" color="error" />
                      </template>
                      <v-list-item-title class="text-error">
                        Удалить
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </v-list-item>
          </v-list>
        </div>

        <div class="form-row">
          <div class="form-label" />
          <v-btn
            @click="openDialog"
            append-icon="mdi-note-plus"
            variant="text"
            color="primary"
          >
            Добавить документ
          </v-btn>
          <span>(максимальный размер 2МБ; pdf,odt,ods,png,jpeg,jpg)</span>
        </div>

        <v-dialog
          v-model="dialog"
          max-width="500"
          @update:model-value="onDialogClose"
        >
          <v-card>
            <v-card-title>Добавить документ</v-card-title>

            <v-card-text>
              <v-alert
                v-if="dialogAlertMessage"
                :type="dialogAlertType"
                :text="dialogAlertMessage"
                density="compact"
                variant="tonal"
                closable
                class="mb-4"
                @click:close="clearDialogAlert"
              />

              <v-select
                v-model="selectedDocType"
                :items="receivedDocuments"
                item-title="name"
                item-value="key"
                label="Тип документа"
                variant="outlined"
                density="compact"
                :disabled="uploadLoading"
              />

              <input
                ref="fileInput"
                type="file"
                accept=".pdf,.odt,.ods,.png,.jpeg,.jpg"
                @change="handleFileUpload"
                :disabled="uploadLoading"
              />

              <div v-if="fileName" style="margin-top: 8px">
                Файл: {{ fileName }}
              </div>

              <div v-if="fileSizeError" class="text-error text-caption mt-2">
                {{ fileSizeError }}
              </div>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn text @click="closeDialog" :disabled="uploadLoading">
                Отмена
              </v-btn>
              <v-btn
                color="primary"
                @click="submitDocument"
                :loading="uploadLoading"
                :disabled="uploadLoading"
              >
                Сохранить
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import PassportFields from "moodleDir/procollege/lib/vue/passport.vue";
import { VMaskInput } from "vuetify/labs/VMaskInput";

export default {
  name: "PersonalDocuments",

  components: {
    PassportFields,
    VMaskInput,
  },

  props: {
    student: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      passportData: {},
      dialog: false,
      selectedDocType: null,
      file: null,
      fileName: "",
      fileSizeError: null,
      receivedDocuments: [],
      scan: [],
      loading: false,
      uploadLoading: false,
      alertMessage: null,
      alertType: "info",
      dialogAlertMessage: null,
      dialogAlertType: "info",
    };
  },

  watch: {
    student: {
      handler(newVal) {
        if (newVal && newVal.passport) {
          this.passportData = { ...newVal.passport };
        } else {
          this.passportData = {};
        }
      },
      immediate: true,
      deep: true,
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    loadData() {
      this.isLoading = true;

      const promises = [this.loadDirectory(), this.getScanList()];

      Promise.allSettled(promises).finally(() => {
        this.isLoading = false;
      });
    },

    loadDirectory() {
      return this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/core_procollege/helpers/DirectoryLoader/Load",
          new URLSearchParams({
            directory: JSON.stringify(["receivedDocuments"]),
          })
        )
        .then((res) => {
          this.receivedDocuments = [];
          for (let i in res.data.receivedDocuments) {
            this.receivedDocuments.push({
              key: Number(res.data.receivedDocuments[i].id),
              name: res.data.receivedDocuments[i].name,
            });
          }
        })
        .catch((error) => {
          console.error("Ошибка загрузки справочника:", error);
          this.showAlert(
            "Произошла ошибка при загрузке типов документов",
            "error"
          );
          throw error;
        });
    },

    getScanList() {
      if (!this.$route.params.personal_id) {
        return Promise.resolve();
      }

      this.loading = true;

      return this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/studentCard/getScanList",
          new URLSearchParams({
            personal_id: this.$route.params.personal_id,
          })
        )
        .then((res) => {
          this.scan = res.data || [];
        })
        .catch((error) => {
          console.error("Ошибка при загрузке списка:", error);
          this.scan = [];
          this.showAlert(
            "Произошла ошибка при загрузке списка сканов",
            "error"
          );
          throw error;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onFieldUpdate(field, value) {
      this.$emit("update:student", {
        ...this.student,
        [field]: value,
      });
    },

    onPassportUpdate(value) {
      this.passportData = value;
      this.$emit("update:student", {
        ...this.student,
        passport: value,
      });
    },

    openDialog() {
      if (!this.$route.params.personal_id) {
        this.showAlert("Сначала сохраните личное дело", "warning");
        return;
      }
      this.dialog = true;
      this.clearDialogAlert();
      this.fileSizeError = null;
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();
      this.clearDialogAlert();
      this.fileSizeError = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    onDialogClose(value) {
      if (!value) {
        this.closeDialog();
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        this.fileSizeError = "Размер файла не должен превышать 2MB";
        this.file = null;
        this.fileName = "";
        event.target.value = "";
        return;
      }

      this.fileSizeError = null;
      this.file = file;
      this.fileName = file.name;
      this.clearDialogAlert();
    },

    submitDocument() {
      this.clearDialogAlert();
      this.fileSizeError = null;

      if (!this.$route.params.personal_id) {
        this.showDialogAlert("Ошибка: не найден ID студента", "error");
        return;
      }

      if (!this.selectedDocType) {
        this.showDialogAlert("Выберите тип документа", "error");
        return;
      }

      if (!this.file) {
        this.showDialogAlert("Выберите файл", "error");
        return;
      }

      this.uploadLoading = true;
      this.clearAlert();

      const formData = new FormData();
      formData.append("scan", this.file);
      formData.append("scanType", this.selectedDocType);
      formData.append("personal_id", this.$route.params.personal_id);

      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/studentCard/uploadScan",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then(() => {
          this.getScanList();
          this.showAlert("Документ успешно загружен", "success");
          this.closeDialog();
        })
        .catch((error) => {
          console.error("Ошибка при загрузке:", error);
          this.showDialogAlert(
            "Произошла ошибка при загрузке документа",
            "error"
          );
        })
        .finally(() => {
          this.uploadLoading = false;
        });
    },

    deleteScan(item) {
      if (
        !confirm(
          `Удалить документ "${this.getDocumentNameById(item.document_id)}"?`
        )
      ) {
        return;
      }

      this.clearAlert();

      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/manage_groups/secretariat/studentCard/deleteScan",
          new URLSearchParams({
            scan_id: item.id,
          })
        )
        .then(() => {
          this.scan = this.scan.filter((s) => s.id !== item.id);
          this.showAlert("Документ удален", "success");
        })
        .catch((error) => {
          console.error("Ошибка при удалении:", error);
          this.showAlert("Произошла ошибка при удалении документа", "error");
        });
    },

    downloadFile(item) {
      this.axios
        .post(
          this.$model.wwwroot +
            "/procollege/core_procollege/helpers/FileHelper/dowloadFileFromDatarood",
          new URLSearchParams({
            filepath: item.path,
          }),
          {
            responseType: "blob",
          }
        )
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = item.data;
          link.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            link.remove();
          }, 100);
        })
        .catch((error) => {
          console.error("Ошибка скачивания:", error);
          this.showAlert("Не удалось скачать файл", "error");
        });
    },

    resetForm() {
      this.selectedDocType = null;
      this.file = null;
      this.fileName = "";
      this.fileSizeError = null;
    },

    getDocumentNameById(documentId) {
      if (!documentId) return "Неизвестный документ";
      const doc = this.receivedDocuments.find((d) => d.key == documentId);
      return doc ? doc.name : `Документ ${documentId}`;
    },

    showAlert(message, type = "info") {
      this.alertMessage = message;
      this.alertType = type;

      if (type === "success") {
        setTimeout(() => {
          this.clearAlert();
        }, 5000);
      }
    },

    clearAlert() {
      this.alertMessage = null;
      this.alertType = "info";
    },

    showDialogAlert(message, type = "info") {
      this.dialogAlertMessage = message;
      this.dialogAlertType = type;
    },

    clearDialogAlert() {
      this.dialogAlertMessage = null;
      this.dialogAlertType = "info";
    },
  },
};
</script>

<style scoped>
.scan-list {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.scan-item {
  border-bottom: 1px solid #f0f0f0;
}

.scan-item:last-child {
  border-bottom: none;
}

.document-title {
  font-weight: 500;
  font-size: 0.9rem;
}

.text-error {
  color: var(--v-error-base, #ff5252);
}
</style>